import { Router, type RouterLocation } from '@vaadin/router';
import { LitElement, css, html } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { routes } from '../routes';
import { setAppRouter } from '../router';
import { StateController } from '../state';
import { pushPageView as pushAnalyticsPageView } from '../functions/analytics-datalayer-events';
import { UmbMarketModalContainer } from '../components/UmbMarketModalContainer';

@customElement('umb-market-app')
export class UmbMarketApp extends LitElement {
  @query('umb-market-modal-container')
  modalContainer?: UmbMarketModalContainer;

  constructor() {
    super();
    // is this a backoffice embed?
    const params = new URL(document.location.toString()).searchParams;

    StateController.current.isBackoffice = params.get('style') === 'backoffice';
    StateController.variant = Math.random() > 0.5 ? 'A' : 'B';

    // use router events to trigger GTM push
    // this does NOT manage package views as those do not trigger a router event
    // provides activeCategory to avoid circular dependency accessing
    // state in analytics-data-events (state => router => analytics-events => state)
    window.addEventListener(
      'vaadin-router-location-changed',
      async (e: CustomEvent<{ router: Router; location: RouterLocation }>) => {
        const { pathname } = e.detail.location;
        const activeCategory = StateController.current.categories?.find(
          x => x.link === pathname
        );

        // when the route changes all modals should be closed - routing between
        // modals does not trigger this event, only moving between views (eg categories)
        this.modalContainer?.closeAll();

        pushAnalyticsPageView(pathname, activeCategory);
        StateController.syncUrlFilters();

        this.scrollTo(0, 0);
      }
    );
  }

  firstUpdated(): void {
    const outlet = this.shadowRoot?.getElementById('outlet');
    const router = new Router(outlet);
    router.setRoutes(routes);
    setAppRouter(router);
  }

  render() {
    return html`
      <umb-market-header></umb-market-header>
      <umb-market-particles><main id="outlet"></main></umb-market-particles>
      <umb-market-footer></umb-market-footer>
      <umb-market-modal-container></umb-market-modal-container>
      <umb-market-loader></umb-market-loader>
    `;
  }

  static styles = [
    css`
      :host {
        display: block;
        position: fixed;
        background: var(--color-body-surface);
        font-size: 18px;
        font-weight: 300;
        line-height: 1.6;
        overflow-x: hidden;
        overflow-y: scroll;
        height: 100%;
        width: 100%;
      }

      :host(.categories-in),
      :host(.overlay-in) {
        overflow-y: hidden;
        padding-right: 18px;
      }

      :host(.categories-in) {
        --open-menu-btn-opacity: 0;
      }

      main {
        max-width: var(--max-width);
        margin: 0 auto;
        padding: var(--xl-spacer) var(--s-spacer) var(--s-spacer);
      }

      :host(.embed) main {
        padding: 0;
        max-width: none;
      }

      @media (min-width: 767px) {
        main {
          padding: var(--m-spacer);
        }
      }
    `,
  ];
}
